import { useState } from 'react';
import { toast } from 'sonner';
import { useApi } from '../../hooks/useApi';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Switch } from '../../components/ui/switch';
import { Badge } from '../../components/ui/badge';
import { Loader2, Plus, X, Users } from 'lucide-react';

const FREQ_OPTIONS = [
  { value: 'immediate', label: 'Immediate (per event)' },
  { value: 'digest_daily', label: 'Daily digest (1 email/day)' },
  { value: 'digest_weekly', label: 'Weekly digest (1 email/week)' },
];

const ROLE_TOGGLES = [
  { key: 'batch_trainers', label: 'Batch trainers' },
  { key: 'org_admins', label: 'Org admins' },
  { key: 'super_admins', label: 'Super admins' },
];

export function RuleCard({ rule, canManage, onSaved }) {
  const { put } = useApi();
  const [draft, setDraft] = useState(rule);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [emailInput, setEmailInput] = useState('');

  const patch = (upd) => { setDraft(d => ({ ...d, ...upd })); setDirty(true); };
  const patchSection = (section, upd) =>
    patch({ [section]: { ...draft[section], ...upd } });

  const addEmail = () => {
    const e = emailInput.trim().toLowerCase();
    if (!e) return;
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(e)) { toast.error('Invalid email'); return; }
    const list = draft.recipients.extra_emails || [];
    if (list.includes(e)) { setEmailInput(''); return; }
    patchSection('recipients', { extra_emails: [...list, e] });
    setEmailInput('');
  };

  const save = async () => {
    setSaving(true);
    try {
      const updated = await put(`/notification-rules/${rule.key}`, {
        enabled: draft.enabled,
        recipients: draft.recipients,
        frequency: draft.frequency,
        triggers: draft.triggers,
      });
      onSaved(updated);
      setDirty(false);
      toast.success(`${rule.name} updated`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const s = rule.supports || {};

  return (
    <div className={`rounded-xl border bg-card p-4 space-y-4 transition-opacity ${draft.enabled ? '' : 'opacity-70'}`}
         data-testid={`rule-card-${rule.key}`}>
      <div className="flex items-start justify-between gap-3">
        <div>
          <h3 className="text-sm font-semibold">{rule.name}</h3>
          <p className="text-xs text-muted-foreground mt-0.5">{rule.description}</p>
        </div>
        <Switch
          checked={draft.enabled}
          disabled={!canManage}
          onCheckedChange={(v) => patch({ enabled: v })}
          data-testid={`rule-toggle-${rule.key}`}
        />
      </div>

      {s.recipients && (
        <div className="space-y-2">
          <div className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
            <Users className="w-3.5 h-3.5" /> Recipients
          </div>
          <div className="flex flex-wrap gap-1.5">
            {ROLE_TOGGLES.map(rt => (
              <button
                key={rt.key} type="button" disabled={!canManage}
                onClick={() => patchSection('recipients', { [rt.key]: !draft.recipients[rt.key] })}
                data-testid={`rule-${rule.key}-recipient-${rt.key}`}
                className={`px-2.5 py-1 rounded-full border text-xs transition-colors ${
                  draft.recipients[rt.key]
                    ? 'border-[var(--lumyn-content)] bg-[var(--lumyn-content)]/10 text-[var(--lumyn-content)] font-medium'
                    : 'text-muted-foreground hover:border-foreground/30'
                }`}
              >{rt.label}</button>
            ))}
          </div>
          <div className="flex flex-wrap gap-1.5">
            {(draft.recipients.extra_emails || []).map(e => (
              <Badge key={e} variant="secondary" className="gap-1 font-normal" data-testid={`rule-${rule.key}-email-${e}`}>
                {e}
                {canManage && (
                  <button type="button" onClick={() => patchSection('recipients', {
                    extra_emails: draft.recipients.extra_emails.filter(x => x !== e),
                  })}>
                    <X className="w-3 h-3" />
                  </button>
                )}
              </Badge>
            ))}
          </div>
          {canManage && (
            <div className="flex gap-1.5">
              <Input
                value={emailInput}
                onChange={e => setEmailInput(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addEmail(); } }}
                placeholder="Add email (internal or external)…"
                className="h-8 text-xs"
                data-testid={`rule-${rule.key}-email-input`}
              />
              <Button type="button" variant="outline" size="sm" className="h-8 px-2" onClick={addEmail}
                      data-testid={`rule-${rule.key}-email-add`}>
                <Plus className="w-3.5 h-3.5" />
              </Button>
            </div>
          )}
        </div>
      )}

      {(s.frequency || s.threshold_pct || s.pending_days) && (
        <div className="grid grid-cols-2 gap-3">
          {s.frequency && (
            <label className="space-y-1 text-xs">
              <span className="text-muted-foreground font-medium">Frequency</span>
              <select
                className="w-full border rounded-md px-2 py-1.5 bg-background text-xs"
                value={draft.frequency.mode}
                disabled={!canManage}
                onChange={e => patchSection('frequency', { mode: e.target.value })}
                data-testid={`rule-${rule.key}-frequency`}
              >
                {FREQ_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </label>
          )}
          {s.frequency && (
            <label className="space-y-1 text-xs">
              <span className="text-muted-foreground font-medium">Cooldown (days, per trainee)</span>
              <Input
                type="number" min={0} max={90}
                value={draft.frequency.cooldown_days}
                disabled={!canManage}
                onChange={e => patchSection('frequency', { cooldown_days: Number(e.target.value) })}
                className="h-8 text-xs"
                data-testid={`rule-${rule.key}-cooldown`}
              />
            </label>
          )}
          {s.threshold_pct && (
            <label className="space-y-1 text-xs">
              <span className="text-muted-foreground font-medium">Attendance threshold (%)</span>
              <Input
                type="number" min={1} max={100}
                value={draft.triggers.threshold_pct ?? 80}
                disabled={!canManage}
                onChange={e => patchSection('triggers', { threshold_pct: Number(e.target.value) })}
                className="h-8 text-xs"
                data-testid={`rule-${rule.key}-threshold`}
              />
            </label>
          )}
          {s.pending_days && (
            <label className="space-y-1 text-xs">
              <span className="text-muted-foreground font-medium">Alert after (days pending)</span>
              <Input
                type="number" min={1} max={60}
                value={draft.triggers.pending_days ?? 7}
                disabled={!canManage}
                onChange={e => patchSection('triggers', { pending_days: Number(e.target.value) })}
                className="h-8 text-xs"
                data-testid={`rule-${rule.key}-pending-days`}
              />
            </label>
          )}
        </div>
      )}

      {!s.recipients && (
        <p className="text-[11px] text-muted-foreground italic">Sent to the trainee directly.</p>
      )}

      {canManage && dirty && (
        <div className="flex justify-end">
          <Button size="sm" onClick={save} disabled={saving} data-testid={`rule-${rule.key}-save`}>
            {saving && <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />}
            Save changes
          </Button>
        </div>
      )}
    </div>
  );
}
